
import { useEffect, useRef, useState } from 'react';

interface UseLazyImageOptions {
  rootMargin?: string;
  threshold?: number;
}

/**
 * Hook that tracks the visibility of an element using IntersectionObserver.
 * Returns a `ref` to be attached to the element and a boolean `isVisible`
 * that becomes `true` when the element enters the viewport.
 */
export function useLazyImage(
  options: UseLazyImageOptions = {}
): { ref: React.RefObject<HTMLDivElement>; isVisible: boolean } {
  const { rootMargin = '300px 0px', threshold = 0 } = options;
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // Fallback for browsers without IntersectionObserver support
    if (!('IntersectionObserver' in window)) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin, threshold }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [rootMargin, threshold]);

  return { ref, isVisible };
}

/**
 * Hook that preloads an image and provides loading / error state.
 *
 * @param src         Image URL to load.
 * @param placeholder Optional placeholder image URL (returned while loading or on error).
 *
 * @returns An object containing:
 *  - `loaded`: boolean – true when the image has successfully loaded.
 *  - `error`: boolean – true if the image failed to load.
 *  - `src`: string – the source that should be used by the `<img>` element
 *    (either the original `src` when loaded, or the `placeholder` when loading / errored).
 */
export const useImageLoader = (src: string, placeholder?: string) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [currentSrc, setCurrentSrc] = useState<string>(placeholder ?? '');

  useEffect(() => {
    if (!src) {
      setError(true);
      return;
    }

    const img = new Image();

    // Add cache‑control hint for specific domain
    if (src.includes('static.readdy.ai')) {
      img.crossOrigin = 'anonymous';
    }

    const handleLoad = () => {
      setLoaded(true);
      setError(false);
      setCurrentSrc(src);
    };

    const handleError = () => {
      setLoaded(false);
      setError(true);
      // Keep the placeholder if provided; otherwise leave the src empty
      if (placeholder) setCurrentSrc(placeholder);
    };

    img.addEventListener('load', handleLoad);
    img.addEventListener('error', handleError);
    img.src = src;

    // Clean‑up listeners on unmount or src change
    return () => {
      img.removeEventListener('load', handleLoad);
      img.removeEventListener('error', handleError);
    };
  }, [src, placeholder]);

  return { loaded, error, src: currentSrc };
};

export default useLazyImage;
